import { Grid } from "@mui/material";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { isLenderRole, isReadonlyRole, isTPORole } from "../../redux/actions/userAction";
import "../EditProject/EditProject.css";
import PhaseInfoBanner from "../SharedComponents/PhaseInfoBanner/PhaseInfoBanner";
import VerticalLinearStepper from "../VerticalLinearStepper/VerticalLinearStepper";
import AttachmentsPage from "./pages/AttachmentsPage";
import { EditFieldsPage } from "./pages/EditFieldsPage";
import SuccessPage from "./pages/SuccessPage";
import { Unauthorized } from "../Unauthorized/Unauthorized";
import useFeatureFlags from "../shared/services/featureToggle/useFeatureFlags";
import withFeatureFlagUpdates from "../shared/services/featureToggle/withFeatureFlagUpdates";
import { CPM_DUPLICATE_REQUEST_CHECK } from "../shared/constants/FeatureToggleKeys";
import { REQUEST_TYPES } from "../shared/constants/RequestTypes";
import useDuplicateRequestCheck from "../shared/hooks/useDuplicateRequestCheck";
import DuplicateRequestModal from "../LenderRequests/Modals/DuplicateRequestModal";

export const LenderEditPhase = (props) => {
  const shouldRedirect = (!isLenderRole() && !isTPORole()) || isReadonlyRole();

  const projectId = props.match.params.project_id;
  const phaseId = props.match.params.phase_id;

  const history = useHistory();
  const { isFeatureEnabled } = useFeatureFlags([CPM_DUPLICATE_REQUEST_CHECK]);
  const { blockingRequest, warningRequest, isLoading: isDuplicateCheckLoading } = useDuplicateRequestCheck(projectId, phaseId, REQUEST_TYPES.LENDER_EDIT_PHASE);
  const [warningDismissed, setWarningDismissed] = useState(false);

  const [phaseInfo, setPhaseInfo] = useState();
  const [stepCount, setStepCount] = useState(0);
  const [requestCallback, setRequestCallback] = useState();
  const [isAuthorized, setIsAuthorized] = useState(true);

  const steps = useMemo(() => {
    const steps = [
      "Edit Phase Details",
      "Attach Documents",
      "Final Confirmation"
    ];

    return steps;
  }, []);

  const decrementCounter = () => setStepCount(stepCount - 1);
  const incrementCounter = (requestCallback) => {
    if (requestCallback) {
      setRequestCallback({callBack: requestCallback});
    }
    setStepCount(stepCount + 1);
  }

  useEffect(() => {   
    if (!phaseInfo) {
      (async () => {  
        const phaseInfo = await getPhaseDetailsById(phaseId);
   
        // Only allow editing for phase type "Legal"
        if (phaseInfo?.phaseType) {
          const phaseType = phaseInfo.phaseType.trim().toLowerCase();
          if (phaseType !== "legal") {
            console.log("Access denied for phase type:", phaseInfo.phaseType);
            setIsAuthorized(false);
            return;
          }
        }
        
        setPhaseInfo(phaseInfo);
      })();
    } 
  }, [phaseInfo]);

  if (shouldRedirect) {
    location.replace("/unauthorized");
    return null;
  }

  async function getPhaseDetailsById(phaseId) {
    const { data: phaseInfo } = await axios.get(
      global.REACT_APP_API_ENDPOINT +
      `/phase/viewPhase/` +
      phaseId
    );

    if (typeof phaseInfo === `string`) {
      console.error(phaseInfo);
      return;
    }

    return phaseInfo;
  }

  const getPage = () => {
    if (!isAuthorized) {
      return <Unauthorized />;
    }

    if (!stepCount) {
      return (
        <EditFieldsPage
          incrementCounter={incrementCounter}
          phaseInfo={phaseInfo}
          stepCount={3}
        />
      );
    }
    
    if (stepCount === 1) {
      return (
        <AttachmentsPage
          phaseId={phaseId}
          projectId={projectId}
          requestCallback={requestCallback?.callBack}
          incrementCounter={incrementCounter}
          decrementCounter={decrementCounter}
        />
      );
    }

    if (stepCount === 2) {
      return (
        <>
          <SuccessPage
            projectId={projectId}
            msg={`A change to phase ${phaseId} was succesfully requested!`}
          />
        </>
      );
    }
      
    return "Step count out of range";
  };

  if (isFeatureEnabled(CPM_DUPLICATE_REQUEST_CHECK) && isDuplicateCheckLoading) {
    return (
      <Grid className="add-new-project" container>
        <Grid item xs={12} style={{ padding: "20px", textAlign: "center" }}>
          <div>Checking for existing requests...</div>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid className="add-new-project" container>
      {isAuthorized && (
        <DuplicateRequestModal
          show={isFeatureEnabled(CPM_DUPLICATE_REQUEST_CHECK) && !!(blockingRequest || (warningRequest && !warningDismissed))}
          blocking={!!blockingRequest}
          existingRequest={blockingRequest ?? warningRequest}
          requestTypeName={REQUEST_TYPES.LENDER_EDIT_PHASE}
          onCancel={() => history.goBack()}
          onProceed={() => {
            console.warn('[DuplicateRequestCheck] user proceeded past warning', { projectId, requestId: warningRequest?.requestId });
            setWarningDismissed(true);
          }}
        />
      )}
      {isAuthorized && (
        <Grid item xs={3} style={{ backgroundColor: "#F2F2F2" }}>
          <VerticalLinearStepper
            steps={steps}
            activeStep={stepCount}
          />
        </Grid>
      )}
      <Grid
        container
        item
        xs={isAuthorized ? 9 : 12}
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        style={{ padding: "20px", border: "solid 1px black" }}
      >
        {isAuthorized && (
          <PhaseInfoBanner phaseId={phaseId} phaseName={phaseInfo?.phaseName} />
        )}
        {getPage()}
      </Grid>
    </Grid>
  );
};

export default withFeatureFlagUpdates(LenderEditPhase, [CPM_DUPLICATE_REQUEST_CHECK]);